// frontend/src/components/cadetes/CadeteCard.tsx
// Card de cadete — iOS HIG refactor; usa BottomSheet (Presentismo), Modal (ficha y edición en Gestión), Lucide icons.

import { useState, useEffect, useRef } from 'react';
import type { Cadete } from '../../lib/api';
import { storage } from '../../services/storage';
import { COMPANIAS } from '../TopAppBar';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Modal } from '@/components/ui/modal';
import { esEfectivoEnFecha, getArgentinaDateString, getArgentinaNow } from '../../lib/utils';
import { iniciarMarcaEnVuelo, FLUSH_MARCAS_EVENT } from '../../lib/marcasEnVuelo';
import { LICENCIA_POR_MOTIVO } from '../../lib/novedades';
import {
  Check, Clock, Medal, DoorOpen, Award, Phone, PhoneCall, History,
  ClipboardX, List, Bell, BellOff, Stethoscope, Activity,
  CalendarDays, ClipboardCheck, AlertTriangle, HelpCircle,
  CloudDownload, Download, Calendar, FileText, CheckCircle2,
  RotateCcw, XCircle, RefreshCw, User, Save, ArrowRightLeft,
  ShieldCheck, Scale, LogOut, UserX, Pencil, type LucideIcon,
} from 'lucide-react';
import { toast } from '../../lib/toast';
import { m } from 'motion/react';
import { SPRING, SPRING_POP } from '../../lib/motion';

interface CadeteCardProps {
  cadete: Cadete;
  estado?: 'presente' | 'ausente' | 'tarde' | null;
  motivo?: string;
  hora?: string | null;
  onEstadoChange: (estado: 'presente' | 'ausente' | 'tarde', motivo?: string) => void | Promise<void>;
  onDesmarcar?: () => void | Promise<void>;
  disabled?: boolean;
  isListadoMode?: boolean;
  licenciaActiva?: string;
  mode?: 'presentismo' | 'gestion';
  // Inyectado por el padre (CadetesList) — NO instanciar useOfflineSync aquí:
  // este componente se renderiza una vez por cadete y el hook dispara un pull
  // completo al servidor en cada montaje (provoca una avalancha de requests).
  sincronizar?: (opts?: { silent?: boolean; pull?: boolean }) => Promise<void>;
  // Fecha (YYYY-MM-DD) de la jornada que se está marcando; por defecto hoy
  fecha?: string;
  // Abre la Ficha de Personal (HistorialModal de CadetesList) para este cadete
  onVerHistorial?: () => void;
}

// Scroll lock and portal modal are handled by <BottomSheet> (see @/components/ui/bottom-sheet).

const formatMiles = (num: number | string | undefined | null) => {
  if (num === undefined || num === null || num === '') return '—';
  const clean = String(num).replace(/\D/g, '');
  if (!clean) return '—';
  return Number(clean).toLocaleString('es-AR');
};

const formatMilesInput = (value: string) => {
  const clean = value.replace(/\D/g, '');
  if (!clean) return '';
  return Number(clean).toLocaleString('es-AR');
};

const cleanNonDigits = (value: string) => {
  return value.replace(/\D/g, '');
};

const formatNombres = (nombres: string | undefined | null) => {
  if (!nombres) return '';
  const parts = nombres.trim().split(/\s+/);
  if (parts.length <= 1) return nombres;
  const firstName = parts[0];
  const middleNamesAbbreviated = parts.slice(1).map(part => {
    if (!part) return '';
    return `${part.charAt(0).toUpperCase()}.`;
  }).join(' ');
  return `${firstName} ${middleNamesAbbreviated}`;
};

function StatusBadge({ estado, layout = 'inline' }: { estado: string | null | undefined; layout?: 'inline' | 'square' }) {
  if (layout === 'square') {
    const base = "flex-1 h-12 rounded-[14px] flex items-center justify-center gap-2 transition-all border shadow-sm select-none shrink-0 w-full md:w-auto md:px-6";
    
    if (!estado) return (
      <div
        className={base}
        style={{
          backgroundColor: 'rgba(142, 142, 147, 0.08)',
          color: '#8E8E93',
          borderColor: 'rgba(142, 142, 147, 0.22)',
        }}
        title="Pendiente"
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block bg-slate-400 shadow-[0_0_6px_rgba(148,163,184,0.5)] animate-pulse" />
        <span className="text-[9px] font-bold leading-none opacity-80 uppercase tracking-wide">Pendiente</span>
      </div>
    );

    if (estado === 'presente') return (
      <div
        className={base}
        style={{
          backgroundColor: 'rgba(52, 199, 89, 0.08)',
          color: '#248a3d',
          borderColor: 'rgba(52, 199, 89, 0.22)',
        }}
        title="Presente"
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#34C759] shadow-[0_0_6px_rgba(52,199,89,0.5)] animate-pulse" />
        <span className="text-[9px] font-bold leading-none opacity-80 uppercase tracking-wide">Presente</span>
      </div>
    );

    if (estado === 'tarde') return (
      <div
        className={base}
        style={{
          backgroundColor: 'rgba(255, 149, 0, 0.08)',
          color: '#745c00',
          borderColor: 'rgba(255, 149, 0, 0.22)',
        }}
        title="Tardanza"
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#FF9500] shadow-[0_0_6px_rgba(255,149,0,0.5)]" />
        <span className="text-[9px] font-bold leading-none opacity-80 uppercase tracking-wide">Tardanza</span>
      </div>
    );

    return (
      <div
        className={base}
        style={{
          backgroundColor: 'rgba(255, 59, 48, 0.08)',
          color: '#ba1a1a',
          borderColor: 'rgba(255, 59, 48, 0.22)',
        }}
        title="Ausente"
      >
        <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#FF3B30] shadow-[0_0_6px_rgba(255,59,48,0.5)]" />
        <span className="text-[9px] font-bold leading-none opacity-80 uppercase tracking-wide">Ausente</span>
      </div>
    );
  }

  // DEFAULT INLINE LAYOUT (for small metadata tags list under the name)
  const base = "flex items-center gap-1 px-1.5 py-0.5 md:gap-1.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-wider font-extrabold border select-none whitespace-nowrap";

  if (!estado) return (
    <span
      className={`${base} bg-slate-50/90 text-slate-500 border-slate-200/80`}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block bg-slate-400 shadow-[0_0_6px_rgba(148,163,184,0.5)] animate-pulse" />
      Pendiente
    </span>
  );

  if (estado === 'presente') return (
    <span
      className={`${base} bg-emerald-50/90 text-emerald-750 border-emerald-200/80`}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#34C759] shadow-[0_0_6px_rgba(52,199,89,0.5)] animate-pulse" />
      Presente
    </span>
  );

  if (estado === 'tarde') return (
    <span
      className={`${base} bg-amber-50/90 text-amber-800 border-amber-200/80`}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#FF9500] shadow-[0_0_6px_rgba(255,149,0,0.5)]" />
      Tardanza
    </span>
  );

  return (
    <span
      className={`${base} bg-rose-50/90 text-rose-800 border-rose-200/80`}
    >
      <span className="w-1.5 h-1.5 rounded-full inline-block bg-[#FF3B30] shadow-[0_0_6px_rgba(255,59,48,0.5)]" />
      Ausente
    </span>
  );
}

function InstitutionalBadge({ estadoInstitucional }: { estadoInstitucional?: string }) {
  const base = "flex items-center gap-1 px-1.5 py-0.5 md:gap-1.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-wider font-semibold border transition-all duration-200 select-none whitespace-nowrap";

  const current = (estadoInstitucional === 'Aspirante a Cadete' || !estadoInstitucional) ? 'Cadete' : estadoInstitucional;

  let cfg: { bg: string; color: string; border: string; Icon: LucideIcon; label: string } = {
    bg: '#eae8e7', color: '#43474f', border: '#c3c6d1', Icon: User, label: 'Aspirante a Cadete',
  };

  if (current === 'Cadete') {
    cfg = { bg: '#d5e3ff', color: '#001b3c', border: '#799dd6', Icon: ShieldCheck, label: 'Cursante' };
  } else if (current === 'Oficial Recibido') {
    cfg = { bg: '#d1fae5', color: '#065f46', border: '#10b981', Icon: Award, label: 'Oficial Recibido' };
  } else if (current === 'Excluido') {
    cfg = { bg: '#ffdad6', color: '#93000a', border: '#ba1a1a', Icon: Scale, label: 'Excluido' };
  } else if (current === 'Desestimiento') {
    cfg = { bg: '#ffedd5', color: '#9a3412', border: '#f97316', Icon: LogOut, label: 'Desestimiento' };
  } else if (current === 'Ausente a Convocatoria') {
    cfg = { bg: '#f3f4f6', color: '#1f2937', border: '#9ca3af', Icon: UserX, label: 'Ausente Convocatoria' };
  } else if (current === 'Traspaso') {
    cfg = { bg: '#f3e8ff', color: '#6b21a8', border: '#a855f7', Icon: ArrowRightLeft, label: 'Traspaso' };
  }

  return (
    <span
      className={base}
      style={{ backgroundColor: cfg.bg, color: cfg.color, borderColor: cfg.border }}
    >
      <cfg.Icon size={10} strokeWidth={2.2} className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

interface MotivoAusencia {
  id: string;
  label: string;
  Icon: LucideIcon;
  subMotivos?: string[];
}

const MOTIVOS_AUSENCIA: MotivoAusencia[] = [
  { id: 'Ausente con aviso',               label: 'Ausente con aviso',               Icon: Bell },
  { id: 'Ausente sin aviso',               label: 'Ausente sin aviso',               Icon: BellOff },
  { id: 'Licencia por Enfermedad - R.M.H', label: 'Licencia por Enfermedad - R.M.H', Icon: Stethoscope },
  { id: 'A.R.T.',                          label: 'A.R.T.',                          Icon: Activity },
  {
    id: 'Licencia Especial',
    label: 'Licencia Especial',
    Icon: CalendarDays,
    subMotivos: [
      'Art. 50 inc. a) Fallecimiento (Hijos, Padres, Cónyuge o Conviviente)',
      'Art. 50 inc. b) Matrimonio',
      'Art. 50 inc. c) Nacimiento de Hijo',
      'Art. 50 inc. d) Atención de Familiares Enfermos',
      'Art. 50 inc. f) Maternidad',
      'Art. 50 inc. g) Donación de órganos',
    ],
  },
  {
    id: 'Permiso Especial',
    label: 'Permiso Especial',
    Icon: ClipboardCheck,
    subMotivos: [
      'Art. 54. Lactancia.',
      'Art. 56 Razones Particulares',
    ],
  },
];

// Motivos que pueden generar una licencia automática (bloque "Período de Ausencia").
// "Ausente con aviso" / "Ausente sin aviso" NO crean licencia.
const MOTIVOS_CON_PERIODO = Object.keys(LICENCIA_POR_MOTIVO);
// Solo las licencias médicas pueden quedar abiertas "hasta alta médica"
const MOTIVOS_ALTA_MEDICA = ['Licencia por Enfermedad - R.M.H', 'A.R.T.'];

// Tilde del botón P/A: entra con un rebote corto cuando se confirma la marca
function CheckPop({ animar }: { animar: boolean }) {
  return (
    <m.span
      className="flex leading-none"
      initial={animar ? { scale: 0.4, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1, transition: SPRING_POP }}
    >
      <Check size={18} strokeWidth={2.8} />
    </m.span>
  );
}

export function CadeteCard({
  cadete,
  estado,
  motivo,
  hora,
  onEstadoChange,
  onDesmarcar,
  disabled = false,
  isListadoMode = false,
  licenciaActiva,
  mode,
  sincronizar = async () => {},
  fecha,
  onVerHistorial,
}: CadeteCardProps) {
  const fechaJornada = fecha || getArgentinaDateString();
  const [motivoModal, setMotivoModal] = useState(false);
  const [motivoEstado, setMotivoEstado] = useState<'ausente' | 'tarde'>('ausente');
  const [selectedReason, setSelectedReason] = useState<string>('Ausente con aviso');
  const [selectedSubReason, setSelectedSubReason] = useState<string>('');
  const [observaciones, setObservaciones] = useState('');
  const [fechaInicio, setFechaInicio] = useState(fechaJornada);
  const [fechaFin, setFechaFin] = useState('');
  const [isAltaMedica, setIsAltaMedica] = useState(false);
  const [cambioModal, setCambioModal] = useState(false);

  const [llamarOpen, setLlamarOpen] = useState(false);

  // Countdown + animación de salida iOS
  const [pendingAction, setPendingAction] = useState<'presente' | 'ausente' | null>(null);
  const [pendingReason, setPendingReason] = useState<string>('');
  const [countdown, setCountdown] = useState(3);
  const [removing, setRemoving] = useState(false);
  // false en el primer render: los íconos de estado ya marcados no hacen "pop" al cargar la lista
  const montadoRef = useRef(false);
  useEffect(() => { montadoRef.current = true; }, []);
  // Token único por sesión de countdown: keyea el anillo para forzar su remount
  // y que la animación CSS (fill-mode: forwards) reinicie en cada marcado.
  const [pendingSeq, setPendingSeq] = useState(0);
  // Acción pendiente aún NO confirmada. Se asigna solo al iniciar una marca y se
  // pone en null (sincrónicamente) al confirmarla o deshacerla — nunca en render,
  // así un re-render durante el commit no puede re-armarla (doble guardado).
  const pendingActionRef = useRef<'presente' | 'ausente' | null>(null);
  // Liberación del contador global de marcas en vuelo (bloquea recarga/logout)
  const releaseEnVueloRef = useRef<(() => void) | null>(null);
  const commitPendingRef = useRef<(() => Promise<void>) | null>(null);

  const iniciarPending = (action: 'presente' | 'ausente') => {
    // Si ya había una marca sin confirmar se reemplaza: reutiliza su registro en vuelo
    if (!releaseEnVueloRef.current) releaseEnVueloRef.current = iniciarMarcaEnVuelo();
    pendingActionRef.current = action;
    setPendingAction(action);
    setCountdown(3);
    setPendingSeq((s) => s + 1);
  };

  const liberarEnVuelo = () => {
    const release = releaseEnVueloRef.current;
    releaseEnVueloRef.current = null;
    release?.();
  };

  // Perfil e Institutional State management states
  const [perfilModal, setPerfilModal] = useState(false);
  // Formulario de edición (sólo Gestión): modal aparte que se abre desde la ficha
  const [edicionModal, setEdicionModal] = useState(false);
  const [formEstado, setFormEstado] = useState<string>('Cadete');
  const [formFecha, setFormFecha] = useState<string>('');
  const [formMotivo, setFormMotivo] = useState<string>('');
  const [formDescripcion, setFormDescripcion] = useState<string>('');
  const [formCompania, setFormCompania] = useState<string>('');

  // Nuevos estados para Datos Institucionales
  const [formGrado, setFormGrado] = useState<string>('Aspirante a Cadete');
  const [formLegajo, setFormLegajo] = useState<string>('');
  const [formAula, setFormAula] = useState<string>('');
  const [formDomicilio, setFormDomicilio] = useState<string>('');
  const [formComisaria, setFormComisaria] = useState<string>('');

  // Nuevos estados para Datos Personales editables (Gestión)
  const [formApellido, setFormApellido] = useState<string>('');
  const [formNombres, setFormNombres] = useState<string>('');
  const [formDni, setFormDni] = useState<string>('');
  const [formLocalidad, setFormLocalidad] = useState<string>('');
  const [formPartido, setFormPartido] = useState<string>('');
  const [formTelefonoPrincipal, setFormTelefonoPrincipal] = useState<string>('');
  const [formTelefonoAlternativo, setFormTelefonoAlternativo] = useState<string>('');

  // Fecha precargada en "Fecha del Cambio": la guardada sólo si el estado elegido es el
  // que el cadete ya tiene (se está editando esa baja); para un estado nuevo, hoy.
  const fechaInicialDelCambio = (estado: string) => {
    const estadoActual = (cadete.estado_institucional === 'Aspirante a Cadete' || !cadete.estado_institucional) ? 'Cadete' : cadete.estado_institucional;
    return (estado === estadoActual && cadete.estado_metadata?.fecha) || getArgentinaDateString();
  };

  useEffect(() => {
    if (edicionModal) {
      const estadoActual = (cadete.estado_institucional === 'Aspirante a Cadete' || !cadete.estado_institucional) ? 'Cadete' : cadete.estado_institucional;
      setFormEstado(estadoActual);
      setFormFecha(fechaInicialDelCambio(estadoActual));
      setFormMotivo(cadete.estado_metadata?.motivo || '');
      setFormDescripcion(cadete.estado_metadata?.descripcion || '');
      setFormCompania(cadete.estado_metadata?.compania_destino || '');
      
      setFormGrado(cadete.grado || 'Aspirante a Cadete');
      setFormLegajo(cadete.legajo ? formatMilesInput(String(cadete.legajo)) : '');
      setFormAula(cadete.numero_aula ? String(cadete.numero_aula) : '');
      setFormDomicilio(cadete.domicilio || '');
      setFormComisaria(cadete.comisaria_jurisdiccional || '');

      setFormApellido(cadete.apellido || '');
      setFormNombres(cadete.nombres || '');
      setFormDni(cadete.dni ? formatMilesInput(String(cadete.dni)) : '');
      setFormLocalidad(cadete.localidad || '');
      setFormPartido(cadete.partido || '');
      setFormTelefonoPrincipal(cadete.telefono_principal ? cleanNonDigits(cadete.telefono_principal) : '');
      setFormTelefonoAlternativo(cadete.telefono_alternativo ? cleanNonDigits(cadete.telefono_alternativo) : '');
    }
  }, [edicionModal, cadete]);


  const cancelPending = (e: React.MouseEvent) => {
    e.stopPropagation();
    // "Deshacer" explícito: no se debe confirmar la marca ni al desmontar
    pendingActionRef.current = null;
    liberarEnVuelo();
    setPendingAction(null);
    setCountdown(3);
    setRemoving(false);
  };

  const openMotivoModal = (tipo: 'ausente' | 'tarde') => {
    setMotivoEstado(tipo);
    setSelectedReason(tipo === 'ausente' ? 'Ausente con aviso' : 'Demora');
    setSelectedSubReason('');
    setObservaciones('');
    setFechaInicio(fechaJornada);
    setFechaFin('');
    setIsAltaMedica(false);
    setMotivoModal(true);
  };

  const handleDownloadDespacho = () => {
    const fechaHoy = getArgentinaNow().toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    const htmlContent = `
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset='utf-8'>
  <title>Despacho Radiográfico</title>
  <style>
    body { font-family: 'Courier New', Courier, monospace; font-size: 11pt; line-height: 1.4; color: #000; margin: 1in; }
    h1 { text-align: center; font-size: 14pt; font-weight: bold; text-decoration: underline; margin-bottom: 20px; }
    .header-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .header-table td { border: 1px solid #000; padding: 6px; vertical-align: top; }
    .title-cell { font-weight: bold; width: 30%; }
    .content-cell { width: 70%; }
    .text-block { text-align: justify; margin-bottom: 20px; }
    .footer { margin-top: 40px; text-align: right; }
    .signature-line { display: inline-block; width: 220px; border-top: 1px solid #000; text-align: center; margin-top: 40px; font-size: 9pt; }
  </style>
</head>
<body>
  <h1>DESPACHO RADIOGRÁFICO - URGENTE</h1>
  
  <table class="header-table">
    <tr>
      <td class="title-cell">DE:</td>
      <td class="content-cell">SISTEMA DE PRESENTISMO SGCC</td>
    </tr>
    <tr>
      <td class="title-cell">PARA:</td>
      <td class="content-cell">JEFE DE COMPAÑÍA Y CONTROL INSTITUCIONAL</td>
    </tr>
    <tr>
      <td class="title-cell">FECHA DE EMISIÓN:</td>
      <td class="content-cell">${fechaHoy} hs</td>
    </tr>
    <tr>
      <td class="title-cell">ASUNTO:</td>
      <td class="content-cell">INASISTENCIA SIN AVISO - CADETE ${(cadete.apellido ?? '').toUpperCase()}, ${(cadete.nombres ?? '').toUpperCase()}</td>
    </tr>
  </table>

  <div class="text-block">
    POR MEDIO DEL PRESENTE REGISTRO INFORMATIZADO, SE NOTIFICA A LA SUPERIORIDAD QUE EL CADETE ABAJO DETALLADO NO SE HA PRESENTADO A LAS ACTIVIDADES OBLIGATORIAS EN EL DÍA DE LA FECHA, QUEDANDO REGISTRADO EN EL APARTADO DE <strong>AUSENTE SIN AVISO</strong>.
  </div>

  <table class="header-table" style="margin-bottom: 30px;">
    <tr>
      <td class="title-cell">APELLIDO Y NOMBRES:</td>
      <td class="content-cell" style="font-weight: bold;">${(cadete.apellido ?? '').toUpperCase()}, ${(cadete.nombres ?? '').toUpperCase()}</td>
    </tr>
    <tr>
      <td class="title-cell">DNI:</td>
      <td class="content-cell">${cadete.dni}</td>
    </tr>
    <tr>
      <td class="title-cell">LEGAJO:</td>
      <td class="content-cell">${cadete.legajo ?? 'SIN LEGAJO REGISTRADO'}</td>
    </tr>
    <tr>
      <td class="title-cell">GRADO:</td>
      <td class="content-cell">${cadete.grado || 'ASPIRANTE A CADETE'}</td>
    </tr>
    <tr>
      <td class="title-cell">N° DE AULA:</td>
      <td class="content-cell">${cadete.numero_aula ?? 'SIN AULA ASIGNADA'}</td>
    </tr>
    <tr>
      <td class="title-cell">COMPAÑÍA:</td>
      <td class="content-cell">${cadete.compania ?? 'DÉCIMA COMPAÑÍA'}</td>
    </tr>
    <tr>
      <td class="title-cell">DOMICILIO:</td>
      <td class="content-cell">${cadete.domicilio ?? 'SIN DOMICILIO REGISTRADO'}</td>
    </tr>
    <tr>
      <td class="title-cell">LOCALIDAD / PARTIDO:</td>
      <td class="content-cell">${cadete.localidad ?? 'SIN LOCALIDAD'} / ${cadete.partido ?? 'SIN PARTIDO'}</td>
    </tr>
    <tr>
      <td class="title-cell">TELÉFONO PRINCIPAL:</td>
      <td class="content-cell">${cadete.telefono_principal ?? 'SIN TELÉFONO REGISTRADO'}</td>
    </tr>
    <tr>
      <td class="title-cell">COMISARÍA JURISDICCIONAL:</td>
      <td class="content-cell">${cadete.comisaria_jurisdiccional ?? 'SIN COMISARÍA REGISTRADA'}</td>
    </tr>
  </table>

  <div class="text-block">
    SE INICIA EL SEGUIMIENTO ADMINISTRATIVO CORRESPONDIENTE. EL PRESENTE FORMULARIO SE ADJUNTA AL HISTORIAL DE PRESENTISMO DEL CADETE A FIN DE DEJAR CONSTANCIA DEL HECHO.
  </div>

  <div class="footer">
    <div class="signature-line">
      FIRMA DE CONSTATACIÓN DE INSTRUCTOR
    </div>
  </div>
</body>
</html>
    `;

    const blob = new Blob(['\ufeff' + htmlContent], {
      type: 'application/msword;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `despacho_radiografico_${cadete.dni}_${getArgentinaDateString()}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleMotivoConfirm = async () => {
    if (motivoEstado === 'ausente' && MOTIVOS_CON_PERIODO.includes(selectedReason) && !isAltaMedica && fechaFin) {
      if (!fechaInicio) {
        toast.error('Indique la fecha "Desde" del período de ausencia.');
        return;
      }
      if (fechaFin < fechaInicio) {
        toast.error('La fecha "Hasta" no puede ser anterior a "Desde".');
        return;
      }
    }
    let finalReason = selectedReason;
    if (selectedSubReason) {
      finalReason = `${selectedReason}: ${selectedSubReason}`;
    }
    if (observaciones.trim()) {
      finalReason = `${finalReason} (${observaciones.trim()})`;
    }
    
    // Cerrar el modal de motivo
    setMotivoModal(false);

    if (motivoEstado === 'ausente') {
      setPendingReason(finalReason);
      iniciarPending('ausente');
    } else {
      // Registrar estado diario directamente si no es ausente (ej. 'tarde' en el futuro).
      // También cuenta como marca en vuelo mientras espera geolocalización/IndexedDB.
      const release = iniciarMarcaEnVuelo();
      try {
        await onEstadoChange(motivoEstado, finalReason);
      } finally {
        release();
      }
    }
  };

  // Confirma la marca pendiente (presente/ausente + despacho + licencia automática).
  // Estrictamente una vez por acción: la acción y su liberación del contador en
  // vuelo se toman y se limpian sincrónicamente antes del primer await.
  const commitPendingAction = async () => {
    const action = pendingActionRef.current;
    if (!action) return;
    pendingActionRef.current = null;
    const release = releaseEnVueloRef.current;
    releaseEnVueloRef.current = null;

    try {
      await commitAccion(action);
    } finally {
      release?.();
    }
  };

  const commitAccion = async (action: 'presente' | 'ausente') => {
    if (action === 'presente') {
      await onEstadoChange('presente', undefined);
      return;
    }

    await onEstadoChange('ausente', pendingReason);

    // Si es "Ausente sin aviso", gatillar también la descarga del despacho
    if (selectedReason === 'Ausente sin aviso') {
      try {
        handleDownloadDespacho();
      } catch (err) {
        console.error('Error al descargar despacho automáticamente:', err);
      }
    }

    // Licencia automática solo para R.M.H / A.R.T. / Licencia Especial / Permiso
    // Especial (sin fallback: los demás motivos registran solo la ausencia del día).
    // Se crea si quedó abierta (alta médica, solo RMH/ART) o si se indicó "Hasta".
    const code = LICENCIA_POR_MOTIVO[selectedReason];
    const esMedica = MOTIVOS_ALTA_MEDICA.includes(selectedReason);
    const generaLicencia = !!code && ((esMedica && isAltaMedica) || (!!fechaFin && fechaFin >= fechaInicio));
    if (code && generaLicencia) {
      try {
        // Read tipos from local cache (already synced from server)
        const tipos = await storage.getTiposLicencias();
        const tipo = tipos.find(t => t.codigo === code && t.activo !== false);

        if (!tipo) {
          // No usar un tipo arbitrario: la licencia quedaría mal clasificada
          console.error(`No existe el tipo de licencia "${code}" en el catálogo local; no se creó la licencia automática.`);
          toast.error(`La inasistencia se registró, pero no se pudo crear la licencia: el tipo "${code}" no está en el catálogo. Sincronice y cárguela manualmente.`);
          return;
        }

        const licenciaPayload = {
          cadete_id: cadete.id,
          tipo_licencia_id: tipo.id,
          fecha_inicio: fechaInicio,
          fecha_fin: esMedica && isAltaMedica ? null : fechaFin,
          // Siempre el motivo completo con artículo/inciso; las observaciones ya
          // van entre paréntesis al final (ver handleMotivoConfirm).
          motivo_detalle: pendingReason,
        };
        // Save locally optimistically; capture the temp ID to correlate with queue op
        const tempId = await storage.saveLicenciaLocal({
          ...licenciaPayload,
          tipo_nombre: tipo.nombre,
          tipo_codigo: tipo.codigo,
        });
        // Enqueue the server create for the next manual sync
        await storage.enqueueOp({ type: 'createLicencia', payload: { ...licenciaPayload, _tempId: tempId } });
        window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
        if (navigator.onLine) {
          sincronizar({ silent: true, pull: false }).catch((err) => console.error('Error en sincronización automática tras crear licencia automática:', err));
        }
      } catch (err) {
        console.error('Error al crear licencia automática:', err);
      }
    }
  };
  commitPendingRef.current = commitPendingAction;

  // Al desmontar (cambio de filtro/búsqueda/fecha) con una marca en cuenta
  // regresiva: confirmarla ya en lugar de perderla en silencio.
  useEffect(() => {
    // Logout / recarga: confirmar ya la marca en cuenta regresiva
    const onFlush = () => {
      if (pendingActionRef.current && commitPendingRef.current) {
        commitPendingRef.current().catch((err) =>
          console.error('Error al confirmar marca pendiente (flush):', err)
        );
      }
    };
    window.addEventListener(FLUSH_MARCAS_EVENT, onFlush);
    return () => {
      window.removeEventListener(FLUSH_MARCAS_EVENT, onFlush);
      if (pendingActionRef.current && commitPendingRef.current) {
        commitPendingRef.current().catch((err) =>
          console.error('Error al confirmar marca pendiente al desmontar:', err)
        );
      } else {
        // Sin acción pendiente: no dejar el contador en vuelo colgado
        liberarEnVuelo();
      }
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Efecto countdown → animación de salida → commit
  useEffect(() => {
    if (!pendingAction) return;
    if (countdown <= 0) {
      // El anillo ya terminó de vaciarse al llegar a 0 (animación continua de 3s),
      // así que solo dejamos un respiro breve para que el "0" alcance a pulsar y
      // luego colapsamos la tarjeta. Sin esto quedaba un segundo muerto con el "0"
      // y el círculo vacío congelados.
      let commitTimeout: any;
      const collapseTimeout = setTimeout(() => {
        setRemoving(true);
        commitTimeout = setTimeout(async () => {
          try {
            await commitPendingAction();
          } catch (err) {
            console.error('Error al confirmar marca:', err);
          } finally {
            // Si la tarjeta sigue en la lista filtrada (búsqueda, filtro
            // "convocada"...), debe volver a mostrarse con su nuevo estado.
            setPendingAction(null);
            setCountdown(3);
            setRemoving(false);
          }
        }, 380);
      }, 220);

      return () => {
        clearTimeout(collapseTimeout);
        if (commitTimeout) clearTimeout(commitTimeout);
      };
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [pendingAction, countdown]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleDesmarcar = async () => {
    setCambioModal(false);
    const release = iniciarMarcaEnVuelo();
    try {
      await onDesmarcar?.();
    } finally {
      release();
    }
  };

  const handleSavePerfilEstado = async () => {
    if (!formApellido.trim() || !formNombres.trim()) {
      toast.error('Por favor, complete el apellido y nombres del cursante.');
      return;
    }
    const cleanDni = formDni.replace(/\./g, '');
    const parsedDni = parseInt(cleanDni, 10);
    if (isNaN(parsedDni) || parsedDni < 10000000 || parsedDni > 99999999) {
      toast.error('El DNI debe ser un número válido de 8 dígitos (entre 10.000.000 y 99.999.999).');
      return;
    }

    if (formEstado === 'Excluido' && !formDescripcion.trim()) {
      toast.error('Por favor, describa el hecho de la exclusión.');
      return;
    }
    if (formEstado === 'Desestimiento' && !formMotivo.trim()) {
      toast.error('Por favor, ingrese el motivo del desestimiento.');
      return;
    }
    if (formEstado === 'Traspaso' && !formCompania.trim()) {
      toast.error('Por favor, ingrese la compañía de destino.');
      return;
    }
    // La baja rige desde esta fecha en partes y reportes: debe ser una fecha válida
    if (['Excluido', 'Desestimiento', 'Traspaso'].includes(formEstado) && !/^\d{4}-\d{2}-\d{2}$/.test(formFecha)) {
      toast.error('Por favor, indique la fecha desde la que rige el cambio de estado.');
      return;
    }

    // Validar Datos Institucionales
    let legajoVal: number | null = null;
    const cleanLegajo = formLegajo.replace(/\./g, '');
    if (cleanLegajo.trim()) {
      const parsedLegajo = parseInt(cleanLegajo.trim(), 10);
      if (isNaN(parsedLegajo) || parsedLegajo < 100000 || parsedLegajo > 999999) {
        toast.error('El legajo debe ser un número de 6 dígitos (entre 100.000 y 999.999).');
        return;
      }
      legajoVal = parsedLegajo;
    }

    let aulaVal: number | null = null;
    if (formAula.trim()) {
      const parsedAula = parseInt(formAula.trim(), 10);
      if (isNaN(parsedAula) || parsedAula < 1 || parsedAula > 999) {
        toast.error('El número de aula debe ser un número entre 1 y 999.');
        return;
      }
      aulaVal = parsedAula;
    }

    const updatedMetadata: any = {};
    if (['Cadete', 'Oficial Recibido', 'Excluido', 'Desestimiento', 'Traspaso'].includes(formEstado)) {
      updatedMetadata.fecha = formFecha;
    }
    if (formEstado === 'Excluido') {
      updatedMetadata.motivo = formMotivo;
      updatedMetadata.descripcion = formDescripcion;
    }
    if (formEstado === 'Desestimiento') {
      updatedMetadata.motivo = formMotivo;
    }
    if (formEstado === 'Traspaso') {
      updatedMetadata.compania_destino = formCompania;
    }

    const payload = {
      apellido: formApellido.trim().toUpperCase(),
      nombres: formNombres.trim().toUpperCase(),
      dni: String(parsedDni),
      legajo: legajoVal,
      grado: formGrado,
      numero_aula: aulaVal,
      domicilio: formDomicilio.trim() || null,
      localidad: formLocalidad.trim().toUpperCase() || undefined,
      partido: formPartido.trim().toUpperCase() || undefined,
      telefono_principal: formTelefonoPrincipal.trim() || undefined,
      telefono_alternativo: formTelefonoAlternativo.trim() || undefined,
      comisaria_jurisdiccional: formComisaria.trim() || null,
      estado_institucional: formEstado,
      estado_metadata: Object.keys(updatedMetadata).length ? updatedMetadata : null
    };

    let mergedCadete: Cadete = {
      ...cadete,
      apellido: formApellido.trim().toUpperCase(),
      nombres: formNombres.trim().toUpperCase(),
      dni: String(parsedDni),
      estado_institucional: formEstado as any,
      estado_metadata: Object.keys(updatedMetadata).length ? updatedMetadata : undefined,
      legajo: legajoVal ?? undefined,
      grado: formGrado,
      numero_aula: aulaVal ?? undefined,
      domicilio: formDomicilio.trim() || undefined,
      localidad: formLocalidad.trim().toUpperCase() || undefined,
      partido: formPartido.trim().toUpperCase() || undefined,
      telefono_principal: formTelefonoPrincipal.trim() || undefined,
      telefono_alternativo: formTelefonoAlternativo.trim() || undefined,
      comisaria_jurisdiccional: formComisaria.trim() || undefined
    };

    try {
      // Save locally first (offline-first)
      await storage.saveCadeteLocal(mergedCadete);
      // Enqueue the API call for the next manual sync
      await storage.enqueueOp({
        type: 'updateCadete',
        payload: { id: cadete.id, data: payload },
      });
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
      if (navigator.onLine) {
        sincronizar().catch((err) => console.error('Error en sincronización automática tras handleSavePerfilEstado:', err));
      }
      setEdicionModal(false);
    } catch (e) {
      console.error('Error al guardar el estado del cadete localmente:', e);
      toast.error('Hubo un error al guardar los cambios localmente.');
    }
  };

  const subtituloPerfil = `${cadete.grado || 'Aspirante a Cadete'} ${cadete.apellido}, ${cadete.nombres}`;

  // Ficha de sólo lectura (compartida por Presentismo y Gestión)
  const fichaPerfil = (
    <div className="px-4 py-4">
      <div className="space-y-6">
        {onVerHistorial && (
          <button
            type="button"
            className="w-full h-11 rounded-[12px] text-ios-subhead font-semibold flex items-center justify-center gap-2 border tap-active"
            style={{ backgroundColor: '#d5e3ff', color: '#001e40', borderColor: '#799dd6' }}
            onClick={() => { setPerfilModal(false); onVerHistorial(); }}
          >
            <History size={16} strokeWidth={2} />
            Ver historial de asistencia y licencias
          </button>
        )}
        <div className="p-4 rounded-xl border border-[#c3c6d1] bg-[#fbf9f8] space-y-2">
          {/* 1. Grado */}
          <div className="flex justify-between items-center py-1">
            <span className="text-xs font-semibold text-[#737780]">Grado:</span>
            <span className="text-sm font-bold text-[#0369a1] flex items-center gap-1">
              <Medal size={14} strokeWidth={2} />
              {cadete.grado || 'Aspirante a Cadete'}
            </span>
          </div>

          {/* 2. Nombre y Apellido */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Nombre y Apellido:</span>
            <span className="text-sm font-bold text-[#001e40]">{cadete.apellido}, {cadete.nombres}</span>
          </div>

          {/* 3. DNI */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">DNI:</span>
            <span className="text-sm font-bold text-[#001e40]">{formatMiles(cadete.dni)}</span>
          </div>

          {/* 4. Legajo */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Legajo:</span>
            <span className="text-sm text-[#001e40]">{formatMiles(cadete.legajo)}</span>
          </div>

          {/* 5. Domicilio */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Domicilio:</span>
            <span className="text-sm text-[#001e40] truncate max-w-[200px]" title={cadete.domicilio || undefined}>{cadete.domicilio ?? 'No registrado'}</span>
          </div>

          {/* 6. Localidad */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Localidad:</span>
            <span className="text-sm text-[#001e40]">{cadete.localidad ?? 'No registrado'}</span>
          </div>

          {/* 7. Partido */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Partido:</span>
            <span className="text-sm text-[#001e40]">{cadete.partido ?? 'No registrado'}</span>
          </div>

          {/* 8. N° de aula */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">N° de Aula:</span>
            <span className="text-sm text-[#001e40]">{cadete.numero_aula ?? 'No registrado'}</span>
          </div>

          {/* 9. Compañía */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Compañía:</span>
            <span className="text-sm text-[#001e40]">{cadete.compania ?? 'No registrado'}</span>
          </div>

          {/* 10. Teléfono Principal */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Teléfono Principal:</span>
            {cadete.telefono_principal ? (
              <a
                href={`tel:${cadete.telefono_principal}`}
                className="text-sm font-bold text-[#001e40] hover:underline flex items-center gap-1 text-primary"
              >
                <Phone size={14} strokeWidth={2} />
                {cadete.telefono_principal}
              </a>
            ) : (
              <span className="text-sm text-[#737780]">No registrado</span>
            )}
          </div>

          {/* 11. Teléfono Secundario */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Teléfono Secundario:</span>
            {cadete.telefono_alternativo ? (
              <a
                href={`tel:${cadete.telefono_alternativo}`}
                className="text-sm font-bold text-[#001e40] hover:underline flex items-center gap-1 text-primary"
              >
                <Phone size={14} strokeWidth={2} />
                {cadete.telefono_alternativo}
              </a>
            ) : (
              <span className="text-sm text-[#737780]">No registrado</span>
            )}
          </div>

          {/* 12. Comisaría Jurisdiccional */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Comisaría Jurisdiccional:</span>
            <span className="text-sm text-[#001e40] truncate max-w-[200px]" title={cadete.comisaria_jurisdiccional || undefined}>{cadete.comisaria_jurisdiccional ?? 'No registrado'}</span>
          </div>

          {/* Fila de la Situación Académica Local */}
          <div className="flex justify-between items-center border-t border-[#c3c6d1]/40 pt-2 py-1">
            <span className="text-xs font-semibold text-[#737780]">Situación Académica:</span>
            <InstitutionalBadge estadoInstitucional={cadete.estado_institucional} />
          </div>
          
          {/* Show metadata if set */}
          {cadete.estado_institucional && cadete.estado_institucional !== 'Aspirante a Cadete' && cadete.estado_metadata && (
            <div className="mt-2 p-3 rounded-lg bg-[#eae8e7] text-xs space-y-1 text-[#43474f] border border-[#c3c6d1]">
              <p className="font-semibold uppercase tracking-wider text-[10px] text-[#737780]">Detalles del estado actual:</p>
              {cadete.estado_metadata.fecha && (
                <p><strong>Fecha de registro:</strong> {cadete.estado_metadata.fecha}</p>
              )}
              {cadete.estado_metadata.motivo && (
                <p><strong>Motivo:</strong> {cadete.estado_metadata.motivo}</p>
              )}
              {cadete.estado_metadata.descripcion && (
                <p><strong>Descripción:</strong> {cadete.estado_metadata.descripcion}</p>
              )}
              {cadete.estado_metadata.compania_destino && (
                <p><strong>Compañía destino:</strong> {cadete.estado_metadata.compania_destino}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Determinar el color de fondo de la card según el estado
  const getCardStyle = () => {
    if (estado === 'presente') return { borderColor: 'rgba(37,99,235,0.3)', backgroundColor: '#ffffff' };
    if (estado === 'ausente') return { borderColor: 'rgba(186,26,26,0.3)', backgroundColor: '#ffffff' };
    if (estado === 'tarde') return { borderColor: 'rgba(115,92,0,0.3)', backgroundColor: '#ffffff' };
    return { borderColor: '#c3c6d1', backgroundColor: '#ffffff' };
  };

  return (
    <>
      {/* Wrapper de animación iOS — colapsa suavemente cuando removing=true.
          Misma duración que el timer de commit (380 ms): la lógica no depende
          de la animación, solo coinciden. */}
      <m.div
        initial={false}
        animate={removing ? { height: 0, opacity: 0 } : { height: 'auto', opacity: 1 }}
        transition={{ height: { duration: 0.38, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.28, ease: 'easeOut' } }}
        style={{ overflow: 'hidden' }}
      >
      {/* CARD — rounded-xl con border, exacto al Stitch */}
      <div
        className="relative border rounded-xl p-3 md:p-4 flex flex-col gap-3 shadow-sm transition-all hover:border-secondary/50 mb-3"
        style={{ ...getCardStyle() }}
      >
        {/* Ver historial (Top Right, solo gestión) */}
        {mode === 'gestion' && onVerHistorial && (
          <button
            type="button"
            className="absolute top-2.5 right-3 w-9 h-9 rounded-full flex items-center justify-center border transition-all tap-active"
            style={{ backgroundColor: '#f6f3f2', borderColor: '#c3c6d1', color: '#001e40' }}
            title="Ver historial"
            aria-label="Ver historial"
            onClick={(e) => { e.stopPropagation(); onVerHistorial(); }}
          >
            <History size={16} strokeWidth={2} />
          </button>
        )}

        {/* Hora de ingreso (Top Right) */}
        {hora && (estado === 'presente' || estado === 'tarde') && (
          <div
            className="absolute top-2.5 right-3 flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-black border animate-fade-in select-none"
            style={{
              backgroundColor: estado === 'presente' ? '#dbeafe' : '#fed65b',
              color: estado === 'presente' ? '#1e40af' : '#745c00',
              borderColor: estado === 'presente' ? '#bfdbfe' : 'rgba(115,92,0,0.2)',
            }}
          >
            <Clock size={11} strokeWidth={2.5} />
            {hora}
          </div>
        )}

        {/* Responsive flex container: columns on mobile, row on desktop/tablet */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3.5 md:gap-3 flex-1 min-w-0">
          {/* Avatar and Info Wrapper - Clickable in Listado mode to view profile */}
          <div
            className={`flex items-center gap-3 flex-1 min-w-0 ${isListadoMode ? 'cursor-pointer hover:opacity-85' : ''}`}
            onClick={() => {
              if (isListadoMode) {
                setPerfilModal(true);
              }
            }}
          >
            {/* Avatar / iniciales */}
            <div className="relative flex-shrink-0">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 font-bold text-sm"
                style={{
                  backgroundColor: '#f0eded',
                  borderColor: '#c3c6d1',
                  color: '#001e40',
                }}
              >
                {(cadete.apellido ?? '').charAt(0)}
              </div>
              {/* Status dot */}
              <span
                className="absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-white rounded-full"
                style={{
                  backgroundColor: !estado ? '#d1d5db' : estado === 'presente' ? '#22c55e' : estado === 'tarde' ? '#facc15' : '#ef4444',
                }}
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pr-14 md:pr-0">
              <h3
                className="font-bold text-base truncate leading-tight"
                style={{ color: '#001e40', fontFamily: 'Inter, sans-serif' }}
              >
                {cadete.apellido}, {formatNombres(cadete.nombres)}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: '#43474f' }}>
                Legajo: {cadete.legajo ?? '—'} · DNI: {cadete.dni}
              </p>
              {/* Badges container */}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(disabled || mode === 'gestion') && <StatusBadge estado={estado} />}
                <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                  <InstitutionalBadge estadoInstitucional={cadete.estado_institucional} />
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 md:gap-1.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-wider font-extrabold border select-none whitespace-nowrap"
                    style={{
                      backgroundColor: '#e0f2fe',
                      color: '#0369a1',
                      borderColor: '#bae6fd',
                    }}
                  >
                    <Medal size={10} className="w-2.5 h-2.5" strokeWidth={2.2} />
                    {cadete.grado || 'Aspirante a Cadete'}
                  </span>
                </div>
                {cadete.numero_aula && (
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 md:gap-1.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-wider font-extrabold border select-none whitespace-nowrap"
                    style={{
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      borderColor: '#e5e7eb',
                    }}
                  >
                    <DoorOpen size={10} className="w-2.5 h-2.5" strokeWidth={2} />
                    Aula {cadete.numero_aula}
                  </span>
                )}
                {licenciaActiva && (
                  <span
                    className="flex items-center gap-1 px-1.5 py-0.5 md:gap-1.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-[10px] uppercase tracking-wider font-extrabold border select-none whitespace-nowrap"
                    style={{
                      backgroundColor: '#f3e8ff',
                      color: '#6b21a8',
                      borderColor: '#d8b4fe',
                    }}
                  >
                    <Award size={10} className="w-2.5 h-2.5" strokeWidth={2} />
                    Licencia ({licenciaActiva})
                  </span>
                )}
              </div>
              {/* Motivo */}
              {(estado === 'ausente' || estado === 'tarde') && motivo && (
                <p className="text-[11px] italic mt-1.5 leading-tight" style={{ color: '#43474f' }}>
                  Motivo: {motivo}
                </p>
              )}
            </div>
          </div>

          {/* Botones de acción: P · A · Llamar (solo en modo presentismo, ≥44pt iOS touch targets) */}
          {!disabled && mode !== 'gestion' && (() => {
            // Según el estado que regía en la jornada: antes de la fecha de baja todavía se puede marcar
            const altersFuerza = !esEfectivoEnFecha(cadete, fechaJornada);

            return (
              <div className="flex items-center justify-between md:justify-start gap-3 flex-shrink-0 pt-2.5 md:pt-0 border-t border-gray-100 md:border-t-0 w-full md:w-auto">
                <StatusBadge estado={estado} layout="square" />
                <div className="flex items-center gap-2">
                  {/* Con licencia vigente no se marca presente: ya figura por su licencia
                      en el Parte (si ya tenía un presente, se deja el botón para desmarcarlo) */}
                  {!altersFuerza && licenciaActiva && !estado && (
                    <span
                      className="h-12 px-3 rounded-[14px] flex flex-col items-center justify-center text-center leading-tight border"
                      style={{ backgroundColor: '#ede7f6', borderColor: '#d1c4e9', color: '#4527a0' }}
                      title="Con licencia vigente: no se marca presente"
                    >
                      <span className="text-[10px] font-semibold uppercase tracking-wide">En licencia</span>
                      <span className="text-[13px] font-black">{licenciaActiva}</span>
                    </span>
                  )}

                  {/* Presente [P] */}
                  {!altersFuerza && estado !== 'ausente' && (!licenciaActiva || estado === 'presente') && (
                    <m.button
                      whileTap={{ scale: 0.9 }}
                      transition={SPRING}
                      className="w-12 h-12 rounded-[14px] flex flex-col items-center justify-center gap-0.5 transition-colors"
                      style={estado === 'presente' ? {
                        backgroundColor: '#34C759',
                        color: 'white',
                        border: '1px solid rgba(0,120,40,0.22)',
                        boxShadow: '0 3px 10px rgba(52,199,89,0.42), inset 0 1px 0 rgba(255,255,255,0.2)',
                      } : {
                        backgroundColor: 'rgba(52, 199, 89, 0.08)',
                        color: '#248a3d',
                        border: '1px solid rgba(52, 199, 89, 0.22)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.72)',
                      }}
                      title="Presente"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (estado === 'presente') {
                          setCambioModal(true);
                        } else if (!pendingAction) {
                          iniciarPending('presente');
                        }
                      }}
                    >
                      {estado === 'presente'
                        ? <CheckPop animar={montadoRef.current} />
                        : <span className="text-[15px] font-black leading-none h-5 w-full flex items-center justify-center text-center">P</span>
                      }
                      <span className="text-[9px] font-semibold leading-none opacity-80 text-center w-full">
                        {estado === 'presente' ? 'Pres.' : 'Pres.'}
                      </span>
                    </m.button>
                  )}

                  {/* Ausente [A] */}
                  {!altersFuerza && estado !== 'presente' && (
                    <m.button
                      whileTap={{ scale: 0.9 }}
                      transition={SPRING}
                      className="w-12 h-12 rounded-[14px] flex flex-col items-center justify-center gap-0.5 transition-colors"
                      style={estado === 'ausente' ? {
                        backgroundColor: '#FF3B30',
                        color: 'white',
                        border: '1px solid rgba(180,0,0,0.22)',
                        boxShadow: '0 3px 10px rgba(255,59,48,0.42), inset 0 1px 0 rgba(255,255,255,0.2)',
                      } : {
                        backgroundColor: 'rgba(255, 59, 48, 0.08)',
                        color: '#ba1a1a',
                        border: '1px solid rgba(255, 59, 48, 0.22)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.72)',
                      }}
                      title="Ausente"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (estado === 'ausente') {
                          setCambioModal(true);
                        } else {
                          openMotivoModal('ausente');
                        }
                      }}
                    >
                      {estado === 'ausente'
                        ? <CheckPop animar={montadoRef.current} />
                        : <span className="text-[15px] font-black leading-none h-5 w-full flex items-center justify-center text-center">A</span>
                      }
                      <span className="text-[9px] font-semibold leading-none opacity-80 text-center w-full">
                        {estado === 'ausente' ? 'Aus.' : 'Aus.'}
                      </span>
                    </m.button>
                  )}

                  {/* Llamar — bottom sheet estilo iOS */}
                  <button
                    className="w-12 h-12 rounded-[14px] flex flex-col items-center justify-center gap-0.5 transition-all tap-active"
                    style={{
                      backgroundColor: 'rgba(0,122,255,0.08)',
                      color: '#007AFF',
                      border: '1px solid rgba(0,122,255,0.22)',
                      boxShadow: '0 1px 3px rgba(0,122,255,0.1), inset 0 1px 0 rgba(255,255,255,0.72)',
                    }}
                    title="Llamar"
                    onClick={(e) => { e.stopPropagation(); setLlamarOpen(true); }}
                  >
                    <Phone size={18} strokeWidth={1.8} />
                    <span className="text-[9px] font-semibold leading-none opacity-70">Llamar</span>
                  </button>
                </div>

                {/* Bottom sheet "Llamar" */}
                <BottomSheet
                  open={llamarOpen}
                  onClose={() => setLlamarOpen(false)}
                  title="Contactar Cadete"
                  subtitle={`${cadete.apellido}, ${cadete.nombres}`}
                  icon={<Phone size={18} strokeWidth={1.8} />}
                  detent="adaptive"
                >
                  <div className="px-4 py-3 space-y-1">
                    {cadete.telefono_principal ? (
                      <a
                        href={`tel:${cadete.telefono_principal}`}
                        className="flex items-center gap-3 p-3 rounded-[12px] tap-active"
                        style={{ backgroundColor: 'rgba(0,30,64,0.05)' }}
                        onClick={() => setLlamarOpen(false)}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: '#001e40' }}
                        >
                          <Phone size={16} strokeWidth={1.8} style={{ color: '#fed65b' }} />
                        </div>
                        <div>
                          <p className="text-ios-caption1 text-[#8E8E93] font-semibold uppercase tracking-wide">Principal</p>
                          <p className="text-ios-body font-semibold text-[#001e40]">{cadete.telefono_principal}</p>
                        </div>
                      </a>
                    ) : (
                      <p className="text-ios-subhead text-[#8E8E93] px-3 py-2">Principal: no registrado</p>
                    )}
                    {cadete.telefono_alternativo ? (
                      <a
                        href={`tel:${cadete.telefono_alternativo}`}
                        className="flex items-center gap-3 p-3 rounded-[12px] tap-active"
                        style={{ backgroundColor: 'rgba(60,60,67,0.05)' }}
                        onClick={() => setLlamarOpen(false)}
                      >
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ backgroundColor: '#43474f' }}
                        >
                          <PhoneCall size={16} strokeWidth={1.8} style={{ color: 'white' }} />
                        </div>
                        <div>
                          <p className="text-ios-caption1 text-[#8E8E93] font-semibold uppercase tracking-wide">Alternativo</p>
                          <p className="text-ios-body font-semibold text-[#43474f]">{cadete.telefono_alternativo}</p>
                        </div>
                      </a>
                    ) : (
                      <p className="text-ios-subhead text-[#8E8E93] px-3 py-2">Alternativo: no registrado</p>
                    )}
                  </div>
                </BottomSheet>
              </div>
            );
          })()}
        </div>

        {/* Countdown overlay — MOBILE: layout vertical (la card es alta en columna). */}
        {pendingAction && (
          <div
            className="md:hidden absolute inset-0 z-20 rounded-[11px] flex flex-col items-center justify-center backdrop-blur-md p-4 animate-fade-in select-none"
            style={{
              backgroundColor: pendingAction === 'presente' ? 'rgba(240, 253, 244, 0.93)' : 'rgba(254, 242, 242, 0.93)',
              transition: 'background-color 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Countdown circular ring container */}
            <div key={pendingSeq} className="relative w-28 h-28 flex items-center justify-center">
              <svg viewBox="0 0 112 112" className="absolute w-full h-full transform -rotate-90">
                {/* Background track circle */}
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  className="stroke-current"
                  style={{
                    color: pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  }}
                  strokeWidth="5"
                  fill="transparent"
                />
                {/* Animated active progress circle */}
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  className="stroke-current animate-countdown-ring"
                  style={{
                    color: pendingAction === 'presente' ? '#10b981' : '#ef4444',
                    strokeDasharray: 301.6,
                  }}
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="transparent"
                />
              </svg>
              {/* Numeric display in the center */}
              <span
                key={countdown} // Resetting key forces re-render for spring scale-in pulse animation
                className="text-3xl font-black tabular-nums leading-none animate-pulse-scale"
                style={{
                  color: pendingAction === 'presente' ? '#065f46' : '#991b1b',
                  fontFamily: 'Inter, sans-serif'
                }}
              >
                {countdown}
              </span>
            </div>

            {/* Action Text */}
            <div className="text-center mt-3 mb-4">
              <p
                className="text-sm font-bold tracking-tight uppercase"
                style={{ color: pendingAction === 'presente' ? '#065f46' : '#991b1b' }}
              >
                {pendingAction === 'presente' ? 'Registrando Presente' : 'Registrando Ausencia'}
              </p>
              <p className="text-[11px] font-medium text-gray-500 mt-0.5">
                Toca cancelar para deshacer esta acción
              </p>
            </div>

            {/* Cancel Button */}
            <button
              onClick={cancelPending}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-102 active:scale-95"
              style={{
                backgroundColor: pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: pendingAction === 'presente' ? '#047857' : '#b91c1c',
                border: `1px solid ${pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              }}
            >
              <XCircle size={12} strokeWidth={2.5} />
              Cancelar
            </button>
          </div>
        )}

        {/* Countdown overlay — DESKTOP/TABLET: layout horizontal compacto (la card
            es una fila baja en md+, el stack vertical mobile se desbordaba). */}
        {pendingAction && (
          <div
            className="hidden md:flex absolute inset-0 z-20 rounded-[11px] flex-row items-center justify-center gap-4 backdrop-blur-md px-5 animate-fade-in select-none"
            style={{
              backgroundColor: pendingAction === 'presente' ? 'rgba(240, 253, 244, 0.93)' : 'rgba(254, 242, 242, 0.93)',
              transition: 'background-color 0.3s ease',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Anillo compacto */}
            <div key={pendingSeq} className="relative w-14 h-14 shrink-0 flex items-center justify-center">
              <svg viewBox="0 0 56 56" className="absolute w-full h-full transform -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r="22"
                  className="stroke-current"
                  style={{
                    color: pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  }}
                  strokeWidth="4"
                  fill="transparent"
                />
                <circle
                  cx="28"
                  cy="28"
                  r="22"
                  className="stroke-current animate-countdown-ring"
                  style={{
                    color: pendingAction === 'presente' ? '#10b981' : '#ef4444',
                    strokeDasharray: 138.2,
                    ['--ring-circ' as string]: '138.2px',
                  } as React.CSSProperties}
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="transparent"
                />
              </svg>
              <span
                key={countdown}
                className="text-xl font-black tabular-nums leading-none animate-pulse-scale"
                style={{
                  color: pendingAction === 'presente' ? '#065f46' : '#991b1b',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {countdown}
              </span>
            </div>

            {/* Texto */}
            <div className="flex flex-col min-w-0">
              <p
                className="text-sm font-bold tracking-tight uppercase leading-tight"
                style={{ color: pendingAction === 'presente' ? '#065f46' : '#991b1b' }}
              >
                {pendingAction === 'presente' ? 'Registrando Presente' : 'Registrando Ausencia'}
              </p>
              <p className="text-[11px] font-medium text-gray-500 mt-0.5 leading-tight">
                Toca cancelar para deshacer esta acción
              </p>
            </div>

            {/* Cancelar */}
            <button
              onClick={cancelPending}
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-102 active:scale-95"
              style={{
                backgroundColor: pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                color: pendingAction === 'presente' ? '#047857' : '#b91c1c',
                border: `1px solid ${pendingAction === 'presente' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              }}
            >
              <XCircle size={12} strokeWidth={2.5} />
              Cancelar
            </button>
          </div>
        )}
      </div>
      </m.div>{/* /wrapper animación iOS */}

      {/* Modal de motivo (ausente/tarde) → BottomSheet */}
      <BottomSheet
        open={motivoModal}
        onClose={() => setMotivoModal(false)}
        title={motivoEstado === 'ausente' ? 'Motivo de Ausencia' : 'Justificar Tardanza'}
        subtitle={`${cadete.apellido}, ${cadete.nombres}`}
        icon={motivoEstado === 'ausente' ? <ClipboardX size={18} strokeWidth={1.8} /> : <Clock size={18} strokeWidth={1.8} />}
        detent="large"
        footer={
          <div className="flex flex-col gap-2">
            <button
              className="w-full h-12 rounded-[14px] text-ios-subhead font-semibold text-white flex items-center justify-center gap-2 tap-active"
              style={{ backgroundColor: '#001e40' }}
              onClick={handleMotivoConfirm}
            >
              <CheckCircle2 size={18} strokeWidth={2} />
              Confirmar
            </button>
            <button
              className="w-full h-12 rounded-[14px] text-ios-subhead font-medium tap-active"
              style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
              onClick={() => setMotivoModal(false)}
            >
              Cancelar
            </button>
          </div>
        }
      >
        <div className="px-4 py-4 space-y-5">
        <p className="text-ios-subhead border-l-4 border-[#799dd6] pl-3 py-1" style={{ color: '#43474f', backgroundColor: 'rgba(60,60,67,0.05)', borderRadius: '0 8px 8px 0' }}>
          Documentar formalmente el motivo del registro institucional.
        </p>
        <div className="space-y-5">
          {/* Reason Selection */}
          <div className="space-y-3">
            <label className="text-ios-footnote font-semibold text-[#001e40] flex items-center gap-2 uppercase tracking-wide">
              <List size={16} strokeWidth={2} />
              Seleccione el Motivo
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(motivoEstado === 'ausente'
                ? MOTIVOS_AUSENCIA
                : [
                    { id: 'Demora',   label: 'Demora',   Icon: Clock },
                    { id: 'Tránsito', label: 'Tránsito', Icon: AlertTriangle },
                    { id: 'Comisión', label: 'Comisión', Icon: Medal },
                    { id: 'Otro',     label: 'Otro',     Icon: HelpCircle },
                  ] as MotivoAusencia[]
              ).map((item) => {
                const isActive = selectedReason === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className="flex items-center justify-center gap-2 h-12 rounded-[12px] text-ios-subhead font-semibold transition-all tap-active"
                    style={{
                      border: isActive ? '2px solid #001e40' : '1px solid rgba(60,60,67,0.15)',
                      backgroundColor: isActive ? '#d5e3ff' : 'white',
                      color: isActive ? '#001e40' : '#43474f',
                    }}
                    onClick={() => { setSelectedReason(item.id); setSelectedSubReason(''); setIsAltaMedica(false); setFechaFin(''); }}
                  >
                    <item.Icon size={16} strokeWidth={1.8} />
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Sub-motivos selector */}
            {motivoEstado === 'ausente' && MOTIVOS_AUSENCIA.find(m => m.id === selectedReason)?.subMotivos && (
              <div className="space-y-1.5 animate-fade-in mt-3">
                <label className="text-xs font-semibold text-[#737780]" htmlFor="select-sub-motivo">
                  Seleccione el Artículo / Inciso
                </label>
                <select
                  id="select-sub-motivo"
                  className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-semibold text-[#001e40]"
                  value={selectedSubReason}
                  onChange={(e) => setSelectedSubReason(e.target.value)}
                >
                  <option value="">-- Seleccionar inciso --</option>
                  {MOTIVOS_AUSENCIA.find(m => m.id === selectedReason)?.subMotivos?.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Banner Despacho Radiográfico */}
            {motivoEstado === 'ausente' && selectedReason === 'Ausente sin aviso' && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-[12px] flex items-center justify-between text-xs text-red-800 animate-fade-in mt-3">
                <div className="flex items-center gap-2">
                  <CloudDownload size={18} strokeWidth={1.8} className="text-red-600 shrink-0" />
                  <span className="font-semibold">Despacho Radiográfico disponible</span>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadDespacho}
                  className="px-3 py-1.5 bg-red-600 text-white font-bold rounded-[10px] transition-all tap-active flex items-center gap-1.5 shrink-0"
                >
                  <Download size={13} strokeWidth={2} />
                  DOC
                </button>
              </div>
            )}
          </div>

          {/* Selectores de Fecha para Ausencias Prolongadas (Apple-like).
              Solo para los motivos que generan licencia (RMH, ART, LE, PE). */}
          {motivoEstado === 'ausente' && MOTIVOS_CON_PERIODO.includes(selectedReason) && (
            <div className="mt-4 p-4 rounded-xl border border-[#c3c6d1] bg-[#fcfcfc] shadow-sm space-y-4 animate-fade-in">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-[#001e40] flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <Calendar size={16} strokeWidth={2} />
                  Período de Ausencia
                </label>
                {MOTIVOS_ALTA_MEDICA.includes(selectedReason) && (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#737780]">Alta Médica</span>
                    <button
                      type="button"
                      onClick={() => { setIsAltaMedica(!isAltaMedica); setFechaFin(''); }}
                      className={`w-11 h-6 rounded-full flex items-center transition-all duration-200 px-1 ${isAltaMedica ? 'bg-[#16a34a]' : 'bg-[#c3c6d1]'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${isAltaMedica ? 'translate-x-5' : 'translate-x-0'}`} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-[#737780]">Desde</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border text-sm font-bold text-[#001e40] focus:outline-none focus:ring-2 focus:ring-[#001e40]/20 transition-all bg-white"
                    style={{ borderColor: '#e2e4ea', fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-[#737780]">Hasta</label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    disabled={isAltaMedica}
                    className={`w-full h-10 px-3 rounded-lg border text-sm font-bold transition-all bg-white focus:outline-none focus:ring-2 focus:ring-[#001e40]/20 ${isAltaMedica ? 'opacity-50 cursor-not-allowed bg-[#f6f3f2] text-[#737780]' : 'text-[#001e40]'}`}
                    style={{ borderColor: '#e2e4ea', fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
              </div>
              <p className="text-[11px] leading-snug text-[#43474f] border-l-2 border-[#c3c6d1] pl-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                {isAltaMedica
                  ? `Se registrará una licencia ${LICENCIA_POR_MOTIVO[selectedReason]} abierta desde el ${fechaInicio || fechaJornada} hasta el alta médica.`
                  : fechaFin
                    ? `Se registrará la inasistencia de hoy y una licencia ${LICENCIA_POR_MOTIVO[selectedReason]} del ${fechaInicio || fechaJornada} al ${fechaFin}.`
                    : MOTIVOS_ALTA_MEDICA.includes(selectedReason)
                      ? 'Indique "Hasta" o active "Alta Médica" para generar la licencia; si no, se registra solo la inasistencia del día.'
                      : 'La fecha "Hasta" es obligatoria para generar la licencia. Si queda vacía se registra solo la inasistencia del día.'}
              </p>
            </div>
          )}

          {/* Additional Details */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-[#001e40] block flex items-center gap-2" htmlFor="obs" style={{ fontFamily: 'Inter, sans-serif' }}>
              <FileText size={16} strokeWidth={2} />
              {['Licencia por Enfermedad - R.M.H', 'A.R.T.'].includes(selectedReason) ? 'Diagnóstico Médico' : 'Observaciones adicionales'}
            </label>
            <textarea
              id="obs"
              className="w-full p-4 border border-[#c3c6d1] rounded-lg text-sm focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] transition-all outline-none resize-none bg-white"
              style={{ fontFamily: 'Inter, sans-serif', color: '#1b1c1c' }}
              placeholder={['Licencia por Enfermedad - R.M.H', 'A.R.T.'].includes(selectedReason) ? 'Ingrese el diagnóstico médico oficial...' : 'Ingrese detalles específicos del motivo...'}
              rows={3}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>
        </div>
        </div>{/* /px-4 py-4 */}
      </BottomSheet>

      {/* Modal de cambio de estado */}
      <BottomSheet
        open={cambioModal}
        onClose={() => setCambioModal(false)}
        title="Cambiar Estado"
        subtitle={`Cadete ${cadete.apellido}, ${cadete.nombres}`}
        icon={<RefreshCw size={18} strokeWidth={1.8} />}
      >
        <div className="px-4 py-4 space-y-4">
          <p className="text-ios-subhead text-[#43474f]">
            Seleccione una opción para actualizar el estado del cadete.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              className="flex flex-col items-center justify-center gap-2 h-20 rounded-[14px] font-semibold border transition-all tap-active text-xs uppercase"
              style={{ backgroundColor: '#dbeafe', borderColor: '#bfdbfe', color: '#1e40af' }}
              onClick={handleDesmarcar}
            >
              <RotateCcw size={24} strokeWidth={1.8} />
              Volver a Pendiente
            </button>
            <button
              type="button"
              className="flex flex-col items-center justify-center gap-2 h-20 rounded-[14px] font-semibold border transition-all tap-active text-xs uppercase"
              style={{ backgroundColor: '#ffdad6', borderColor: 'rgba(186,26,26,0.2)', color: '#93000a' }}
              onClick={() => { setCambioModal(false); openMotivoModal('ausente'); }}
            >
              <XCircle size={24} strokeWidth={1.8} />
              Ausente
            </button>
          </div>
        </div>
      </BottomSheet>

      {/* Ficha del cadete (sólo lectura). En Gestión el botón Editar abre otro modal con el formulario. */}
      {mode === 'presentismo' ? (
        <BottomSheet
          open={perfilModal}
          onClose={() => setPerfilModal(false)}
          title="Perfil Institucional"
          subtitle={subtituloPerfil}
          icon={<User size={18} strokeWidth={1.8} />}
          detent="large"
          footer={
            <button
              className="w-full h-12 rounded-[14px] text-ios-subhead font-semibold tap-active"
              style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
              onClick={() => setPerfilModal(false)}
            >
              Cerrar
            </button>
          }
        >
          {fichaPerfil}
        </BottomSheet>
      ) : (
        <>
          <Modal
            open={perfilModal}
            onClose={() => setPerfilModal(false)}
            title="Ficha del Cadete"
            subtitle={subtituloPerfil}
            icon={<User size={18} strokeWidth={1.8} />}
            footer={
              <div className="flex flex-col gap-3">
                <button
                  className="w-full h-12 rounded-[14px] text-ios-subhead font-semibold text-white flex items-center justify-center gap-2 tap-active"
                  style={{ backgroundColor: '#001e40' }}
                  onClick={() => { setPerfilModal(false); setEdicionModal(true); }}
                >
                  <Pencil size={16} strokeWidth={2} />
                  Editar
                </button>
                <button
                  className="w-full h-12 rounded-[14px] text-ios-subhead font-medium tap-active"
                  style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
                  onClick={() => setPerfilModal(false)}
                >
                  Cerrar
                </button>
              </div>
            }
          >
            {fichaPerfil}
          </Modal>

          <Modal
            open={edicionModal}
            onClose={() => setEdicionModal(false)}
            title="Editar Perfil"
            subtitle={subtituloPerfil}
            icon={<Pencil size={18} strokeWidth={1.8} />}
            footer={
              <div className="flex flex-col gap-3">
                <button
                  className="w-full h-12 rounded-[14px] text-ios-subhead font-semibold text-white flex items-center justify-center gap-2 tap-active"
                  style={{ backgroundColor: '#001e40' }}
                  onClick={handleSavePerfilEstado}
                >
                  <Save size={16} strokeWidth={2} />
                  Guardar Cambios
                </button>
                <button
                  className="w-full h-12 rounded-[14px] text-ios-subhead font-medium tap-active"
                  style={{ backgroundColor: 'rgba(60,60,67,0.08)', color: '#43474f' }}
                  onClick={() => { setEdicionModal(false); setPerfilModal(true); }}
                >
                  Cancelar
                </button>
              </div>
            }
          >
            <div className="px-4 py-4">
        <div className="space-y-4">
          {/* Situación Académica */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#737780]" htmlFor="select-estado">
              Situación Académica
            </label>
            <select
              id="select-estado"
              className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-semibold text-[#001e40]"
              value={formEstado}
              onChange={(e) => {
                const val = e.target.value as any;
                setFormEstado(val);
                // Cada cambio de estado arranca con fecha de hoy: la fecha guardada es la del
                // estado anterior y, si se arrastra, la baja quedaría retroactiva.
                setFormFecha(fechaInicialDelCambio(val));
                if (val === 'Excluido') {
                  setFormMotivo('Falta Grave');
                } else {
                  setFormMotivo('');
                }
              }}
            >
              <option value="Cadete">Cursante (Conformador de Fuerza Efectiva)</option>
              <option value="Ausente a Convocatoria">Ausente a Convocatoria (A.A.C.)</option>
              <option value="Desestimiento">Desistimiento Voluntario (D.V.)</option>
              <option value="Excluido">Exclusión (EXC.)</option>
              <option value="Traspaso">Movimiento Int. Compañía (M.I.C.)</option>
            </select>
          </div>

          {/* Campos Dinámicos para el Estado Administrativo */}
          {['Cadete', 'Oficial Recibido', 'Excluido', 'Desestimiento', 'Traspaso'].includes(formEstado) && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-fecha">
                Fecha del Cambio
              </label>
              <input
                id="input-fecha"
                type="date"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40] font-medium"
                value={formFecha}
                onChange={(e) => setFormFecha(e.target.value)}
                required
              />
            </div>
          )}

          {formEstado === 'Excluido' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="select-motivo-exclusion">
                Motivo de Exclusión
              </label>
              <select
                id="select-motivo-exclusion"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-semibold text-[#ba1a1a]"
                value={formMotivo}
                onChange={(e) => setFormMotivo(e.target.value)}
              >
                <option value="Falta Grave">Falta Grave</option>
                <option value="Cuestiones de Disciplina">Cuestiones de Disciplina</option>
                <option value="Incumplimiento de Deberes">Incumplimiento de Deberes</option>
                <option value="Rendimiento Académico Insuficiente">Rendimiento Académico Insuficiente</option>
                <option value="Examen Médico/Físico No Aprobado">Examen Médico/Físico No Aprobado</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          )}

          {formEstado === 'Excluido' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-descripcion">
                Descripción del Hecho
              </label>
              <textarea
                id="input-descripcion"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all resize-none"
                placeholder="Detalle los hechos que motivaron la exclusión..."
                rows={2}
                value={formDescripcion}
                onChange={(e) => setFormDescripcion(e.target.value)}
                required
              />
            </div>
          )}

          {formEstado === 'Desestimiento' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-motivo-desestimiento">
                Motivo de Desestimiento
              </label>
              <input
                id="input-motivo-desestimiento"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all"
                placeholder="Ej: Problemas de salud, Cuestiones familiares..."
                value={formMotivo}
                onChange={(e) => setFormMotivo(e.target.value)}
                required
              />
            </div>
          )}

          {formEstado === 'Traspaso' && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="select-compania-destino">
                Compañía a la que fue Trasladado/a
              </label>
              <select
                id="select-compania-destino"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-semibold text-[#001e40]"
                value={formCompania}
                onChange={(e) => setFormCompania(e.target.value)}
                required
              >
                <option value="" disabled>Seleccionar Compañía...</option>
                {COMPANIAS.map((cia) => (
                  <option key={cia} value={cia}>
                    {cia}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Datos Personales e Institucionales Unificados */}
          <div className="border-t border-[#c3c6d1] pt-4 space-y-4">
            <h4 className="font-bold text-sm text-[#001e40] uppercase tracking-wider flex items-center gap-1.5 font-headline">
              <Award size={16} strokeWidth={2} />
              Perfil Institucional
            </h4>

            {/* Grado */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="select-grado">
                Grado
              </label>
              <select
                id="select-grado"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-semibold text-[#0369a1]"
                value={formGrado}
                onChange={(e) => setFormGrado(e.target.value)}
              >
                <option value="Aspirante a Cadete">Aspirante a Cadete</option>
                <option value="Cadete">Cadete</option>
                <option value="Oficial">Oficial</option>
                <option value="Sargento">Sargento</option>
                <option value="Subteniente">Subteniente</option>
                <option value="Teniente">Teniente</option>
                <option value="Teniente Primero">Teniente Primero</option>
                <option value="Oficial Subayudante">Oficial Subayudante</option>
                <option value="Oficial Ayudante">Oficial Ayudante</option>
                <option value="Oficial Subinspector">Oficial Subinspector</option>
                <option value="Oficial Inspector">Oficial Inspector</option>
                <option value="Oficial Principal">Oficial Principal</option>
              </select>
            </div>

            {/* Apellido */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-apellido">
                Apellido
              </label>
              <input
                id="input-apellido"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all uppercase font-bold text-[#001e40]"
                placeholder="Apellido"
                value={formApellido}
                onChange={(e) => setFormApellido(e.target.value)}
              />
            </div>

            {/* Nombres */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-nombres">
                Nombres
              </label>
              <input
                id="input-nombres"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all uppercase font-bold text-[#001e40]"
                placeholder="Nombres"
                value={formNombres}
                onChange={(e) => setFormNombres(e.target.value)}
              />
            </div>

            {/* DNI (Formato de miles) */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-dni">
                DNI
              </label>
              <input
                id="input-dni"
                type="text"
                inputMode="numeric"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-bold text-[#001e40]"
                placeholder="Ej: 40.344.685"
                value={formDni}
                onChange={(e) => setFormDni(formatMilesInput(e.target.value))}
              />
            </div>

            {/* Legajo (Formato de miles) */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-legajo">
                Legajo
              </label>
              <input
                id="input-legajo"
                type="text"
                inputMode="numeric"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all font-bold text-[#001e40]"
                placeholder="Ej: 422.711"
                value={formLegajo}
                onChange={(e) => setFormLegajo(formatMilesInput(e.target.value))}
              />
            </div>

            {/* Domicilio */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-domicilio">
                Domicilio
              </label>
              <input
                id="input-domicilio"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40]"
                placeholder="Ej: Calle 23 N° 2447 e/ 81 y 82"
                value={formDomicilio}
                onChange={(e) => setFormDomicilio(e.target.value)}
              />
            </div>

            {/* Localidad */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-localidad">
                Localidad
              </label>
              <input
                id="input-localidad"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all uppercase text-[#001e40]"
                placeholder="Localidad"
                value={formLocalidad}
                onChange={(e) => setFormLocalidad(e.target.value)}
              />
            </div>

            {/* Partido */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-partido">
                Partido
              </label>
              <input
                id="input-partido"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all uppercase text-[#001e40]"
                placeholder="Partido"
                value={formPartido}
                onChange={(e) => setFormPartido(e.target.value)}
              />
            </div>

            {/* Aula N° */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-aula">
                Aula N°
              </label>
              <input
                id="input-aula"
                type="text"
                inputMode="numeric"
                maxLength={3}
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40]"
                placeholder="Ej: 30"
                value={formAula}
                onChange={(e) => setFormAula(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            {/* Teléfono Principal */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-tel-principal">
                Teléfono Principal (Números Corridos, sin guiones ni puntos)
              </label>
              <input
                id="input-tel-principal"
                type="text"
                inputMode="tel"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40]"
                placeholder="Ej: 2215984578"
                value={formTelefonoPrincipal}
                onChange={(e) => setFormTelefonoPrincipal(cleanNonDigits(e.target.value))}
              />
            </div>

            {/* Teléfono Secundario */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-tel-secundario">
                Teléfono Secundario (Números Corridos, sin guiones ni puntos)
              </label>
              <input
                id="input-tel-secundario"
                type="text"
                inputMode="tel"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40]"
                placeholder="Ej: 2216548795"
                value={formTelefonoAlternativo}
                onChange={(e) => setFormTelefonoAlternativo(cleanNonDigits(e.target.value))}
              />
            </div>

            {/* Comisaría Jurisdiccional */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-[#737780]" htmlFor="input-comisaria">
                Comisaría Jurisdiccional
              </label>
              <input
                id="input-comisaria"
                type="text"
                className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all text-[#001e40]"
                placeholder="Ej: Comisaría 1ra"
                value={formComisaria}
                onChange={(e) => setFormComisaria(e.target.value)}
              />
            </div>
          </div>
        </div>
            </div>
          </Modal>
        </>
      )}
    </>
  );
}
